<template src="./index.html"></template>

<script>
import Vue from 'vue'
import { parseISO, format } from 'date-fns'
import { ko as koDateFns } from 'date-fns/locale'
import { ko } from 'vuejs-datepicker/dist/locale'
import moment from 'moment'
import optionData from '@/optionData/data'
import agTableComp from '@/optionData/agTableComp'

const STORAGE_KEY_VISIBLE_PLATFORMS = 'albumDeliveryTracker.visiblePlatforms'
const DOMESTIC_ONLY_TAG = 't_only_korea'

/** Full-width date section banner. Rendered as an AG Grid full-width row whenever
 * the underlying row data is a `__dateHeader` placeholder. Spans the entire grid
 * (including under the pinned columns) so the date reads as a clear section break. */
const DateBanner = Vue.extend({
  name: 'dateBanner',
  template: `
    <div class="date-banner">
      <a v-if="releaseDate"
         class="date-banner__label date-banner__link"
         :href="albumListHref"
         target="_blank"
         rel="noopener"
         :title="'앨범 목록 열기 — ' + releaseDate">{{ formattedDate }}</a>
      <span v-else class="date-banner__label">{{ formattedDate }}</span>
    </div>
  `,
  computed: {
    data() { return (this.params && this.params.data) || {} },
    releaseDate() { return this.data.release_date || '' },
    formattedDate() {
      const v = this.releaseDate
      if (!v) return ''
      try {
        return format(parseISO(v), 'yyyy-MM-dd (eee)', { locale: koDateFns })
      } catch (e) { return v }
    },
    albumListHref() {
      const d = encodeURIComponent(this.releaseDate)
      return `/album?date_field=release_date&date_start=${d}&date_end=${d}`
    },
  },
})

export default {
  name: 'albumDeliveryTracker',

  data() {
    return {
      ko,
      moment,
      optionData,

      // filters
      keywordOption: [
        { value: 'name', label: '앨범명' },
        { value: 'artist', label: '아티스트' },
        { value: 'barcode', label: 'UPC/EAN' },
        { value: 'album_ref', label: '앨범코드' },
      ],
      keywordType: 'name',
      keyword: '',
      startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      endDate: moment().add(1, 'month').format('YYYY-MM-DD'),

      // platforms
      contracts: [],
      visiblePlatformIds: [],

      // data
      tableData: [],
      total: 0,
      filtered: 0,

      // server-side sort (driven by AG Grid column header clicks)
      sortField: 'release_date',
      sortOrder: 'desc',
      page: {
        pageSize: 50,
        currentPage: 1,
        totalPage: 0,
      },

      // memo modal
      memoData: {
        albumId: null,
        albumName: '',
        list: [],
        newMemoText: '',
      },

      defaultColDef: {
        sortable: false,
        editable: false,
        resizable: true,
        suppressMenu: true,
      },
      gridOptions: {
        frameworkComponents: { ...agTableComp, dateBanner: DateBanner },
        fullWidthCellRenderer: 'dateBanner',
        isFullWidthCell: (rowNode) => !!(rowNode.data && rowNode.data.__dateHeader),
        context: { componentParent: this },
        suppressCellSelection: true,
        ensureDomOrder: true,
        getRowHeight: (params) => {
          if (params.data && params.data.__dateHeader) return 30
          return 44
        },
        headerHeight: 32,
        onSortChanged: (event) => {
          const model = event.api && typeof event.api.getSortModel === 'function'
            ? event.api.getSortModel()
            : []
          if (model.length > 0) {
            this.sortField = model[0].colId
            this.sortOrder = model[0].sort
          } else {
            this.sortField = 'release_date'
            this.sortOrder = 'desc'
          }
          this.page.currentPage = 1
          this.callList()
        },
        onGridReady: (params) => {
          this.gridApi = params.api
          if (this.columnDefs && this.columnDefs.length) {
            this.gridApi.setColumnDefs(this.columnDefs)
          }
        },
      },
      gridApi: null,
      columnDefs: [],
    }
  },

  created() {
    this.loadVisiblePlatformIds()
    if (this.$route.query.page) this.page.currentPage = Number(this.$route.query.page)
    if (this.$route.query.keywordType) this.keywordType = this.$route.query.keywordType
    if (this.$route.query.keyword) this.keyword = this.$route.query.keyword
    if (this.$route.query.date_start) this.startDate = this.$route.query.date_start
    if (this.$route.query.date_end) this.endDate = this.$route.query.date_end
  },

  mounted() {
    this.$nextTick(() => {
      this.contractCallList()
      this.callList()
    })
  },

  computed: {
    // Fixed-height grid so the AG Grid sticky header stays visible while the rows
    // scroll internally. Subtracts the rough vertical chrome (filter card + page
    // header + paginator) and ensures a sensible minimum.
    gridHeight() {
      const chrome = 360
      const min = 360
      return `max(${min}px, calc(100vh - ${chrome}px))`
    },
  },

  watch: {
    'page.currentPage'() { this.callList() },
    'page.pageSize'() { this.list_search() },
    visiblePlatformIds() {
      this.persistVisiblePlatformIds()
      this.applyColumnDefs()
    },
  },

  methods: {
    readPersistedVisiblePlatformIds() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_VISIBLE_PLATFORMS)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : null
      } catch (e) {
        return null
      }
    },
    loadVisiblePlatformIds() {
      const persisted = this.readPersistedVisiblePlatformIds()
      if (persisted !== null) this.visiblePlatformIds = persisted
    },
    persistVisiblePlatformIds() {
      try {
        localStorage.setItem(STORAGE_KEY_VISIBLE_PLATFORMS, JSON.stringify(this.visiblePlatformIds))
      } catch (e) { /* ignore */ }
    },

    contractCallList() {
      this.$store.dispatch('GET_EXPORT_CONTRACT_V2')
        .then(result => {
          const list = Array.isArray(result) ? result : (result && result.items) || []
          this.contracts = list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          // Only override the visible-platform selection the first time the page loads
          // (no localStorage entry yet). After that we respect the user's choice.
          const persisted = this.readPersistedVisiblePlatformIds()
          if (persisted === null) {
            this.visiblePlatformIds = this.contracts
              .filter(c => c.is_default === true)
              .map(c => c.id)
          }
          this.applyColumnDefs()
        })
        .catch(error => {
          const status = error.response ? error.response.status : error.status
          if (status === 401) {
            this.$store.dispatch('TOKEN_ITEM_CLEAR').then(() => { this.$router.push('/login') })
          } else {
            this.alert({ msg: (error.response && error.response.data && error.response.data.message) || '계약사 목록을 불러오지 못했습니다.' })
          }
        })
    },

    list_search() {
      this.page.currentPage = 1
      this.callList()
    },

    resetSearch() {
      this.keywordType = 'name'
      this.keyword = ''
      this.startDate = moment().subtract(1, 'month').format('YYYY-MM-DD')
      this.endDate = moment().add(1, 'month').format('YYYY-MM-DD')
      this.page.currentPage = 1
      this.callList()
    },

    setDatesToToday() {
      const today = moment().format('YYYY-MM-DD')
      this.startDate = today
      this.endDate = today
      this.page.currentPage = 1
      this.callList()
    },

    callList() {
      const option = {
        page: this.page.currentPage - 1,
        limit: this.page.pageSize,
        sort: this.sortField || 'release_date',
        sortorder: this.sortOrder || 'desc',
        hide_sftp_bulk: 'true',
        has_release_date: 'true',
        exclude_states: 'trash,registration_canceled',
      }
      const query = {}
      if (this.page.currentPage !== 1) query.page = this.page.currentPage
      if (this.keywordType && this.keyword) {
        option[this.keywordType] = this.keyword
        query.keywordType = this.keywordType
        query.keyword = this.keyword
      }
      if (!this.valCheck(this.startDate) && !this.valCheck(this.endDate)) {
        option.date_field = 'release_date'
        option.date_start = this.startDate
        option.date_end = this.endDate
        query.date_start = this.startDate
        query.date_end = this.endDate
      }

      this.$router.replace({ query }).catch(() => {})
      this.loadingShow()
      this.$store.dispatch('GET_ALBUM_DELIVERY_TRACKER', option)
        .then(result => {
          const items = (result.items || []).filter(this.isForeignAlbum)
          this.total = result.total || 0
          this.filtered = result.filtered || items.length
          const remainder = parseInt(this.filtered % this.page.pageSize) > 0 ? 1 : 0
          this.page.totalPage = parseInt(this.filtered / this.page.pageSize) + remainder

          const normalised = items.map(album => ({
            ...album,
            __deliveries: this.normaliseDeliveries(album.deliveries),
          }))
          this.tableData = this.buildGroupedRows(normalised)
          this.loadingHide()
        })
        .catch(error => {
          this.loadingHide()
          const status = error.response ? error.response.status : error.status
          if (status === 401) {
            this.$store.dispatch('TOKEN_ITEM_CLEAR').then(() => { this.$router.push('/login') })
          } else {
            this.alert({ msg: (error.response && error.response.data && error.response.data.message) || '앨범 목록을 불러오지 못했습니다.' })
          }
        })
    },

    isForeignAlbum(album) {
      const tags = album.tags_v2 || []
      return !tags.includes(DOMESTIC_ONLY_TAG)
    },

    normaliseDeliveries(deliveries) {
      if (!deliveries || typeof deliveries !== 'object') return {}
      const out = {}
      for (const [contractId, summary] of Object.entries(deliveries)) {
        if (!summary || !summary.delivered) continue
        out[contractId] = {
          delivered: true,
          delivered_at: summary.delivered_at || null,
          action: summary.action || null,
        }
      }
      return out
    },

    buildGroupedRows(rows) {
      // Server already orders by sort param; for the grouping to read correctly we
      // need rows of the same release_date consecutive. When the user sorts by
      // release_date this is the case; for other sorts we still group locally.
      const sorted = [...rows].sort((a, b) => {
        const ad = a.release_date || ''
        const bd = b.release_date || ''
        if (ad === bd) return 0
        return ad < bd ? 1 : -1
      })

      const out = []
      let currentDate = null
      for (let i = 0; i < sorted.length; i++) {
        const row = sorted[i]
        const date = row.release_date || ''
        if (date !== currentDate) {
          out.push({ __dateHeader: true, release_date: date })
          currentDate = date
        }
        out.push(row)
      }
      return out
    },

    buildColumnDefs() {
      const baseLeft = [
        {
          headerName: '앨범명',
          field: 'name',
          width: 230,
          pinned: 'left',
          cellClass: 'selectable-text',
          cellRenderer: (data) => {
            const row = data.data || {}
            const id = row.id
            const albumName = this.escapeHtml(data.value || '')
            const artists = row.artists || []
            const artistText = this.escapeHtml(
              artists.map(a => a.display_name || a.name || '').filter(Boolean).join(', ')
            )
            const nameMarkup = id
              ? `<a href="/album/detail/${id}" target="_blank" class="blueText" style="text-decoration:underline;">${albumName}</a>`
              : `<span>${albumName}</span>`
            const time = this.normaliseReleaseTime(row.release_time)
            // Tier the time visually:
            //   18:00 → primetime drop, loudest element on the line (warm accent, semibold)
            //   12:00 → DB default, muted to recede
            //   other → present but subordinate
            let timeMarkup = ''
            if (time) {
              let timeClass = 'album-cell__time'
              if (time === '18:00') timeClass += ' album-cell__time--prime'
              else if (time === '12:00') timeClass += ' album-cell__time--default'
              timeMarkup = `<span class="${timeClass}">${time}</span>`
            }
            const { label: stateLabel, cls: stateCls } = this.statePresentation(row.state)
            const statePill = `<span class="album-cell__state-pill state-pill ${stateCls}">${stateLabel}</span>`
            return `<div class="album-cell">` +
              `<div class="album-cell__name">${nameMarkup}${statePill}</div>` +
              `<div class="album-cell__meta">` +
                `<span class="album-cell__sub album-cell__sub--inline">${artistText}</span>` +
                timeMarkup +
              `</div>` +
              `</div>`
          },
        },
        {
          headerName: 'UPC',
          field: 'barcode',
          colId: 'barcode',
          width: 130,
          sortable: true,
          suppressMovable: false,
          cellClass: 'identity-divider selectable-text',
          headerClass: 'identity-divider-header',
          cellRenderer: (data) => {
            const v = data.value
            return `<div class="cellCenter"><span>${v ? this.escapeHtml(v) : '-'}</span></div>`
          },
        },
      ]

      const visibleSet = new Set(this.visiblePlatformIds)
      const platformCols = this.contracts
        .filter(c => visibleSet.has(c.id))
        .map(c => ({
          headerName: c.name,
          field: `__deliveries.${c.id}`,
          colId: `platform_${c.id}`,
          headerClass: 'platform-header',
          width: 70,
          headerTooltip: c.name,
          cellRenderer: (data) => {
            const cell = data.data && data.data.__deliveries ? data.data.__deliveries[c.id] : null
            if (cell && cell.delivered) {
              const tip = cell.delivered_at ? this.fixDate(cell.delivered_at, 'yyyy-MM-dd HH:mm') : ''
              const { label, color } = this.deliveryActionPresentation(cell.action)
              const isSingleChar = label.length === 1
              const size = isSingleChar ? 16 : 12
              const spacing = isSingleChar ? '0' : '0.3px'
              return `<div class="cellCenter"><span title="${tip}" style="color:${color}; font-weight:700; font-size:${size}px; letter-spacing:${spacing};">${label}</span></div>`
            }
            return `<div class="cellCenter"><span style="color:#bbb; font-weight:700; font-size:16px;">X</span></div>`
          },
        }))

      const memoCol = {
        headerName: '비고',
        field: 'id',
        width: 80,
        pinned: 'right',
        cellClass: 'memo-cell',
        headerClass: 'memo-cell-header',
        cellRendererParams: {
          clicked: function (data) {
            this.context.componentParent.memoEditOpen(data)
          },
        },
        cellRendererSelector: (data) => {
          const albumId = data.data.id
          const albumName = data.data.name
          return {
            component: 'btnSelect',
            params: {
              id: albumId,
              album_name: albumName,
              title: '메모',
            },
          }
        },
      }

      return [...baseLeft, ...platformCols, memoCol]
    },

    joinMultilang(multilang) {
      if (!multilang || typeof multilang !== 'object') return ''
      const values = Object.values(multilang)
        .filter(v => typeof v === 'string' && v.trim().length > 0)
      const unique = Array.from(new Set(values.map(v => v.trim())))
      return unique.join(' / ')
    },

    // release_time arrives as either "HH:mm:ss" (TIME column) or a full ISO if the
     // backend ever switches to DateTime. Trim to "HH:mm" in both cases.
    normaliseReleaseTime(raw) {
      if (!raw) return ''
      if (typeof raw === 'string') {
        // "HH:mm:ss" or "HH:mm"
        const m = raw.match(/^(\d{2}):(\d{2})/)
        if (m) return `${m[1]}:${m[2]}`
        // ISO timestamp fallback
        try { return format(parseISO(raw), 'HH:mm') } catch (e) { return raw }
      }
      return ''
    },

    statePresentation(state) {
      const map = {
        temporary:             { label: '임시저장', cls: 'state-pill--temporary' },
        incomplete:            { label: '미완성',   cls: 'state-pill--incomplete' },
        registration_complete: { label: '접수',     cls: 'state-pill--registered' },
        inspection_complete:   { label: '검수완료', cls: 'state-pill--inspected' },
        distribution_complete: { label: '유통완료', cls: 'state-pill--distributed' },
        release_complete:      { label: '발매완료', cls: 'state-pill--released' },
        suspended:             { label: '유통중단', cls: 'state-pill--suspended' },
        rejected:              { label: '반려',     cls: 'state-pill--rejected' },
        registration_canceled: { label: '유통취소', cls: 'state-pill--canceled' },
        trash:                 { label: '삭제',     cls: 'state-pill--trash' },
      }
      return map[state] || { label: state || '-', cls: '' }
    },

    deliveryActionPresentation(action) {
      // Live/active deliveries (insert/update/update_xml) collapse to a single O.
      // Takedown stays distinct so it visually flags a removal.
      if (action === 'takedown') return { label: 'TD', color: '#d33f49' }
      return { label: 'O', color: '#239351' }
    },

    applyColumnDefs() {
      this.columnDefs = this.buildColumnDefs()
      if (this.gridApi) {
        this.gridApi.setColumnDefs(this.columnDefs)
      }
    },

    escapeHtml(text) {
      if (text == null) return ''
      const div = document.createElement('div')
      div.textContent = String(text)
      return div.innerHTML
    },

    memoEditOpen(rowData) {
      this.memoData.albumId = rowData.id
      this.memoData.albumName = rowData.album_name || rowData.name || ''
      this.memoData.list = []
      this.memoData.newMemoText = ''
      this.openModal('deliveryMemo')
      this.loadMemos()
    },

    loadMemos() {
      if (!this.memoData.albumId) return
      const payload = {
        id: `${this.memoData.albumId}`,
        params: {
          sort: 'created_at',
          sortorder: 'desc',
          page: 0,
          limit: 50,
          type: 'delivery',
        },
      }
      this.$store.dispatch('GET_ALBUM_MEMO_V2', payload)
        .then(result => {
          const body = result.data || result
          this.memoData.list = body.items || []
        })
        .catch(() => {
          this.memoData.list = []
        })
    },

    memoAdd() {
      if (this.valCheck(this.memoData.newMemoText)) {
        this.alert({ msg: '메모 내용을 입력해주세요.' })
        return
      }
      const payload = {
        id: `${this.memoData.albumId}`,
        params: {
          text: this.memoData.newMemoText,
          type: 'delivery',
        },
      }
      this.$store.dispatch('POST_ALBUM_MEMO_V2', payload)
        .then(() => {
          this.memoData.newMemoText = ''
          this.noti('메모가 추가되었습니다.')
          this.loadMemos()
        })
        .catch(error => {
          this.alert({ msg: (error.response && error.response.data && error.response.data.message) || '메모 추가에 실패했습니다.' })
        })
    },

    memoDelete(memoId) {
      this.confirm({
        title: '메모 삭제 확인',
        msg: '선택하신 메모를 삭제하시겠습니까?',
        autofocus: true,
        width: 400,
        action: () => {
          const payload = {
            id: `${this.memoData.albumId}`,
            memo_id: memoId,
          }
          this.$store.dispatch('DELETE_ALBUM_MEMO_V2', payload)
            .then(() => {
              this.noti('메모가 삭제되었습니다.')
              this.loadMemos()
            })
            .catch(error => {
              this.alert({ msg: (error.response && error.response.data && error.response.data.message) || '메모 삭제에 실패했습니다.' })
            })
        },
      })
    },

    fmtMemoDate(val) {
      if (!val) return ''
      try { return format(parseISO(val), 'yyyy-MM-dd HH:mm') } catch (e) { return val }
    },
  },
}
</script>

<style scoped>
.platform-toggle {
  min-width: 260px;
}
</style>

<style>
.albumDeliveryTracker-grid {
  font-size: 12px;
}
/* Excel-style click-drag text selection — but only on the identity columns where
   copying makes sense (release date, album name/artist, UPC). Platform O/X/TD
   cells stay non-selectable so a vertical drag in UPC doesn't pull X/O text in. */
.albumDeliveryTracker-grid,
.albumDeliveryTracker-grid .ag-root,
.albumDeliveryTracker-grid .ag-body-viewport,
.albumDeliveryTracker-grid .ag-center-cols-container,
.albumDeliveryTracker-grid .ag-row {
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
}
.albumDeliveryTracker-grid .ag-cell.selectable-text,
.albumDeliveryTracker-grid .ag-cell.selectable-text * {
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  cursor: text;
}
.albumDeliveryTracker-grid .ag-cell:not(.selectable-text),
.albumDeliveryTracker-grid .ag-cell:not(.selectable-text) * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
}
.albumDeliveryTracker-grid .ag-header-cell-label {
  font-size: 12px;
}
.albumDeliveryTracker-grid .ag-header-cell.platform-header .ag-header-cell-label {
  font-size: 10px;
  letter-spacing: 0.02em;
}
.albumDeliveryTracker-grid .ag-cell {
  padding-left: 8px !important;
  padding-right: 8px !important;
  line-height: 1.4;
}
.albumDeliveryTracker-grid .ag-cell .cellCenter {
  font-size: 12px;
}
/* Album-cell time chip — lives inline on the artist/meta row of the album cell.
   The information is tiered:
     • 18:00 (K-pop primetime drop) is the loudest element in the cell, even
       louder than the album title. Burnt-orange accent, tabular nums, a thin
       inset divider so it reads as its own visual unit.
     • 12:00 (database default) is the quietest — almost-transparent, tracked,
       lowercase 'h' literal omitted, optically secondary to the artist name.
     • Anything else sits in between: dim but visible. */
.albumDeliveryTracker-grid .album-cell__meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  line-height: 1.25;
}
.albumDeliveryTracker-grid .album-cell__sub--inline {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.albumDeliveryTracker-grid .album-cell__time {
  flex: 0 0 auto;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  color: rgba(40, 30, 60, 0.7);
  font-weight: 600;
}
.albumDeliveryTracker-grid .album-cell__time--default {
  color: rgba(40, 30, 60, 0.32);
  font-weight: 500;
  letter-spacing: 0.04em;
}
.albumDeliveryTracker-grid .album-cell__time--prime {
  position: relative;
  color: #c2410c;
  font-weight: 700;
  font-size: 11px;
  padding-left: 9px;
}
.albumDeliveryTracker-grid .album-cell__time--prime::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #f97316;
  box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.18);
}

/* Full-width section banner inserted between release-date groups. The banner
   spans the entire grid width (under pinned columns too) so the date reads
   as a clear horizontal break in the data. Smaller, tracked, sticky-feeling. */
.albumDeliveryTracker-grid .ag-full-width-row {
  background: rgba(115, 103, 240, 0.06);
  border-top: 1px solid rgba(115, 103, 240, 0.18);
  border-bottom: 1px solid rgba(115, 103, 240, 0.18);
}
.albumDeliveryTracker-grid .ag-full-width-row .ag-cell-wrapper {
  height: 100%;
}
.albumDeliveryTracker-grid .date-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
  padding: 0 12px;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(20, 20, 40, 0.85);
}
.albumDeliveryTracker-grid .date-banner__label {
  font-variant-numeric: tabular-nums;
}
.albumDeliveryTracker-grid .date-banner__link {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dashed rgba(115, 103, 240, 0.45);
  transition: color 120ms ease, border-color 120ms ease;
}
.albumDeliveryTracker-grid .date-banner__link:hover,
.albumDeliveryTracker-grid .date-banner__link:focus {
  color: rgba(60, 50, 130, 1);
  border-bottom-color: rgba(115, 103, 240, 0.9);
  outline: none;
}
.albumDeliveryTracker-grid .cellRight {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  width: 100%;
}
.albumDeliveryTracker-grid .ag-cell .vs-button {
  padding: 4px 10px !important;
  min-width: 0 !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
}
.albumDeliveryTracker-grid .ag-cell.memo-cell {
  padding-right: 4px !important;
}
.albumDeliveryTracker-grid .ag-cell.memo-cell .cellCenter {
  align-items: flex-end;
}
.albumDeliveryTracker-grid .ag-header-cell.memo-cell-header .ag-header-cell-label {
  justify-content: flex-end;
}
.albumDeliveryTracker-grid .ag-cell.identity-divider,
.albumDeliveryTracker-grid .ag-header-cell.identity-divider-header {
  border-right: 1px solid rgba(0, 0, 0, 0.18) !important;
}
.albumDeliveryTracker-grid .album-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  line-height: 1.25;
}
.albumDeliveryTracker-grid .album-cell__name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 5px;
}
.albumDeliveryTracker-grid .album-cell__state-pill {
  flex: 0 0 auto;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
  vertical-align: middle;
}
.albumDeliveryTracker-grid .state-pill--temporary      { background: #fef3c7; color: #92400e; }
.albumDeliveryTracker-grid .state-pill--incomplete     { background: #fee2e2; color: #991b1b; }
.albumDeliveryTracker-grid .state-pill--registered     { background: #dbeafe; color: #1e40af; }
.albumDeliveryTracker-grid .state-pill--inspected      { background: #ccfbf1; color: #115e59; }
.albumDeliveryTracker-grid .state-pill--distributed    { background: #dcfce7; color: #166534; }
.albumDeliveryTracker-grid .state-pill--released       { background: #ede9fe; color: #5b21b6; }
.albumDeliveryTracker-grid .state-pill--suspended      { background: #ffedd5; color: #9a3412; }
.albumDeliveryTracker-grid .state-pill--rejected       { background: #fee2e2; color: #991b1b; }
.albumDeliveryTracker-grid .state-pill--canceled       { background: #f3f4f6; color: #6b7280; }
.albumDeliveryTracker-grid .state-pill--trash          { background: #fecaca; color: #7f1d1d; }
.albumDeliveryTracker-grid .album-cell__sub {
  font-size: 10.5px;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.albumDeliveryTracker-card .vx-card__body {
  padding: 0.75rem !important;
}

/* ── Filter form ──────────────────────────────────────────────────────────
   Two-column grid: labels in column 1 (auto-sized to the longest label so
   "플랫폼 표시" never wraps), inputs in column 2. Every input row starts on
   the same vertical axis. Labels recede typographically (small, uppercase,
   tracked) so the eye lands on the controls first. */
.albumDeliveryTracker-filters-card .vx-card__body {
  padding: 1rem 1.25rem !important;
}
.tracker-filters {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 1rem;
  row-gap: 0.75rem;
  align-items: start;
}
.tracker-filters__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  user-select: none;
  margin: 0;
  /* Align label to the vertical center of the first input row (≈38px tall).
     Single-row rows shift the input into the middle of the grid cell as well,
     so the label and the first input keep the same centerline whether the row
     wraps (platform tags) or not (dates, search). */
  padding-top: 13px;
}
.tracker-filters__row {
  min-height: 38px;
}
.tracker-filters__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.tracker-filters__row--dates .tracker-filters__date {
  flex: 0 1 180px;
  min-width: 0;
}
.tracker-filters__today {
  flex: 0 0 auto;
  margin-left: 0.5rem !important;
}
.tracker-filters__range-sep {
  color: rgba(0, 0, 0, 0.4);
  padding: 0 0.25rem;
}
.tracker-filters__platforms {
  flex: 1 1 auto;
  min-width: 0;
}
/* Chip text + the deselect (×) glyph were sitting on different baselines —
   the × sat lower than the label. Force the chip to inline-flex so both
   children share a single centerline. */
.tracker-filters__platforms .vs__selected {
  display: inline-flex;
  align-items: center;
  height: 24px;
  line-height: 1;
  padding-top: 0;
  padding-bottom: 0;
  box-sizing: border-box;
}
.tracker-filters__platforms .vs__selected .vs__deselect {
  display: inline-flex;
  align-items: center;
  margin-top: 0;
}
.tracker-filters__row--search .tracker-filters__search-type {
  flex: 0 0 180px;
}
.tracker-filters__row--search .tracker-filters__search-input {
  flex: 1 1 320px;
  max-width: 420px;
}
.tracker-filters__row--search .vs-button {
  flex: 0 0 auto;
}
/* Trim default Vuesax button gutters so they sit flush with the gap */
.tracker-filters__row--search .vs-button {
  margin: 0 !important;
}

/* ── Dark theme overrides ─────────────────────────────────────────────────
   The light-mode palette leans on `rgba(0, 0, 0, …)` and `rgba(40, 30, 60, …)`
   for subdued text and dividers. On the navy dark background those collapse
   to invisible. Re-tint everything off the dark text color so the tiering
   (album name → artist → release time, primetime accent, date banner) still
   reads as a hierarchy. */
.theme-dark .albumDeliveryTracker-grid .album-cell__sub {
  color: rgba(194, 198, 220, 0.62);
}
.theme-dark .albumDeliveryTracker-grid .album-cell__time {
  color: rgba(194, 198, 220, 0.55);
}
.theme-dark .albumDeliveryTracker-grid .album-cell__time--default {
  /* 12:00 is the DB default and repeats on nearly every row — drop it almost
     to background so the eye skips past it and lands on artist + 18:00 chips. */
  color: rgba(194, 198, 220, 0.22);
  font-weight: 400;
}
.theme-dark .albumDeliveryTracker-grid .album-cell__time--prime {
  color: #fdba74;
}
.theme-dark .albumDeliveryTracker-grid .album-cell__time--prime::before {
  background: #fb923c;
  box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.22);
}
.theme-dark .albumDeliveryTracker-grid .ag-full-width-row {
  background: rgba(115, 103, 240, 0.14);
  border-top-color: rgba(115, 103, 240, 0.32);
  border-bottom-color: rgba(115, 103, 240, 0.32);
}
.theme-dark .albumDeliveryTracker-grid .date-banner {
  color: rgba(235, 238, 253, 0.78);
}
.theme-dark .albumDeliveryTracker-grid .date-banner__link {
  border-bottom-color: rgba(180, 170, 255, 0.45);
}
.theme-dark .albumDeliveryTracker-grid .date-banner__link:hover,
.theme-dark .albumDeliveryTracker-grid .date-banner__link:focus {
  color: #ffffff;
  border-bottom-color: rgba(200, 195, 255, 0.95);
}
.theme-dark .albumDeliveryTracker-grid .ag-cell.identity-divider,
.theme-dark .albumDeliveryTracker-grid .ag-header-cell.identity-divider-header {
  border-right-color: rgba(255, 255, 255, 0.08) !important;
}
/* The "X" / "O" / "TD" glyphs are inline-styled on the cellRenderer. Re-tint
   them via attribute selectors so we don't have to touch the renderer:
     • X (no delivery) collapses to a near-invisible whisper — it's the
       background state, not the signal.
     • O (delivered) shifts to a calmer mint that doesn't strobe on navy.
     • TD (takedown) stays warm but loses the high-saturation red bite. */
.theme-dark .albumDeliveryTracker-grid .ag-cell .cellCenter span[style*="color:#bbb"] {
  color: rgba(255, 255, 255, 0.85) !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}
.theme-dark .albumDeliveryTracker-grid .ag-cell .cellCenter span[style*="color:#239351"] {
  color: #34d399 !important;
}
.theme-dark .albumDeliveryTracker-grid .ag-cell .cellCenter span[style*="color:#d33f49"] {
  color: #fb7185 !important;
}
/* Loud "-" placeholders in the UPC column — soften so the row's center of
   gravity stays on the album title. */
.theme-dark .albumDeliveryTracker-grid .ag-cell.identity-divider .cellCenter span {
  color: rgba(194, 198, 220, 0.55);
}
.theme-dark .albumDeliveryTracker-grid .state-pill--temporary      { background: rgba(254, 243, 199, 0.15); color: #fcd34d; }
.theme-dark .albumDeliveryTracker-grid .state-pill--incomplete     { background: rgba(254, 226, 226, 0.12); color: #fca5a5; }
.theme-dark .albumDeliveryTracker-grid .state-pill--registered     { background: rgba(219, 234, 254, 0.12); color: #93c5fd; }
.theme-dark .albumDeliveryTracker-grid .state-pill--inspected      { background: rgba(204, 251, 241, 0.12); color: #5eead4; }
.theme-dark .albumDeliveryTracker-grid .state-pill--distributed    { background: rgba(220, 252, 231, 0.12); color: #86efac; }
.theme-dark .albumDeliveryTracker-grid .state-pill--released       { background: rgba(237, 233, 254, 0.12); color: #c4b5fd; }
.theme-dark .albumDeliveryTracker-grid .state-pill--suspended      { background: rgba(255, 237, 213, 0.12); color: #fdba74; }
.theme-dark .albumDeliveryTracker-grid .state-pill--rejected       { background: rgba(254, 226, 226, 0.12); color: #fca5a5; }
.theme-dark .albumDeliveryTracker-grid .state-pill--canceled       { background: rgba(243, 244, 246, 0.08); color: rgba(194, 198, 220, 0.55); }
.theme-dark .albumDeliveryTracker-grid .state-pill--trash          { background: rgba(254, 202, 202, 0.12); color: #f87171; }
/* Album-title links inherit a saturated `.blueText` from global styles. On the
   navy background that reads as electric. Re-tint to a softer periwinkle that
   still passes as "this is a link". */
.theme-dark .albumDeliveryTracker-grid .album-cell__name a.blueText {
  color: #a5b4fc !important;
}
.theme-dark .albumDeliveryTracker-grid .album-cell__name a.blueText:hover {
  color: #c7d2fe !important;
}
/* Gentle row separators + subtle hover so the eye can track across the wide
   table without the cells feeling boxed-in. */
.theme-dark .albumDeliveryTracker-grid .ag-row {
  border-bottom-color: rgba(255, 255, 255, 0.04) !important;
}
.theme-dark .albumDeliveryTracker-grid .ag-row:hover {
  background-color: rgba(115, 103, 240, 0.06) !important;
}
.theme-dark .tracker-filters__label {
  color: rgba(194, 198, 220, 0.72);
}
.theme-dark .tracker-filters__range-sep {
  color: rgba(194, 198, 220, 0.5);
}
</style>
